import type { ApiResponse, PagedResponseDto } from "@/types/common";
import type {
  ReferralCodeValidationDto,
  ReferralEarningFilterParams,
  ReferralEarningResponseDto,
  ReferralLevelConfigCreateDto,
  ReferralLevelConfigDto,
  ReferralLinkResponseDto,
  ReferralNetworkDto,
  ReferralSummaryDto,
} from "@/types/referral.dto";
import { apiClient, unwrapApiResponse } from "./httpClient";
import type { ReferralType } from "@/types/enums";

export const referralsService = {
  getMyLink(storeId?: number): Promise<ReferralLinkResponseDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<ReferralLinkResponseDto>>("/Referrals/my-link", {
        params: storeId ? { storeId } : undefined,
      }),
    );
  },

  getSummary(): Promise<ReferralSummaryDto> {
    return unwrapApiResponse(apiClient.get<ApiResponse<ReferralSummaryDto>>("/Referrals/summary"));
  },

  getNetwork(maxLevel = 5): Promise<ReferralNetworkDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<ReferralNetworkDto>>("/Referrals/network", {
        params: { maxLevel },
      }),
    );
  },

  getEarnings(filter: ReferralEarningFilterParams = {}): Promise<PagedResponseDto<ReferralEarningResponseDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<ReferralEarningResponseDto>>>("/Referrals/earnings", {
        params: filter,
      }),
    );
  },

  getLevelConfigs(type?: ReferralType): Promise<ReferralLevelConfigDto[]> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<ReferralLevelConfigDto[]>>("/Referrals/levels", {
        params: type !== undefined ? { type } : undefined,
      }),
    );
  },

  createLevelConfig(dto: ReferralLevelConfigCreateDto): Promise<ReferralLevelConfigDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<ReferralLevelConfigDto>>("/Referrals/levels", dto),
    );
  },

  updateLevelConfig(configId: number, dto: ReferralLevelConfigCreateDto): Promise<ReferralLevelConfigDto> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<ReferralLevelConfigDto>>(`/Referrals/levels/${configId}`, dto),
    );
  },

  toggleLevelConfig(configId: number, isActive: boolean): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<boolean>>(`/Referrals/levels/${configId}/toggle`, null, {
        params: { isActive },
      }),
    );
  },

  deleteLevelConfig(configId: number): Promise<boolean> {
    return unwrapApiResponse(apiClient.delete<ApiResponse<boolean>>(`/Referrals/levels/${configId}`));
  },

  validateCode(referralCode: string): Promise<ReferralCodeValidationDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<ReferralCodeValidationDto>>(
        `/Referrals/validate/${encodeURIComponent(referralCode.trim())}`,
      ),
    );
  },
};
